import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
} from "react";
import styled from "@emotion/styled";
import SensorIcon from "components/gateways/SensorIcon";
import { DeviceInfo } from "types";
import dateTimeFormatter from "utils/dateTimeFormatter";
import { getSensorLastReading } from "service/gatewayService";
import {
    addDecimalPlace,
    formatValueWithUnit,
    isHttpSuccess,
    toFiniteReadingNumber,
} from "utils/functions";
import CardBar, { asPercentage } from "./CardBar";
import { SlotProvider, SlotWrapper } from "context/SlotContext";
import { Overlay, Popover } from "react-bootstrap";
import { RowBox } from "components/common";
import {
    SensorContent,
    IconCircle,
    SensorInfo,
    SensorLabel,
    SensorType,
    ReadingTimeText,
} from ".";
import { isUndefined } from "lodash";
import { fetchSensorMetricData } from "service/proqueryService";
import moment from "moment";
import useChartWidth from "hooks/useChartWidth";
import { formatReadingValue } from "utils/chart";
import { ChartType } from "constant/chart";
import { getReadingDateTimeToDisplay } from "utils/gatewayFunctions";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

const DefaultMin = 0;
const DefaultMax = 100;

const SingleCardContent = styled.div<{ $hasFooter?: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    margin-top: auto;

    &.grid {
        display: grid;
        grid-template-areas: ${({ $hasFooter }) =>
        $hasFooter
            ? `"content content value"
            "bar bar bar"
            "status status status"
            "reading reading reading"
            "footer footer footer"`
            : `"content content value"
            "bar bar bar"
            "status status status"
            "reading reading reading"`};
        grid-template-rows: ${({ $hasFooter }) =>
        $hasFooter ? "auto 1fr auto auto auto" : "auto 1fr auto auto"};
        grid-template-columns: 1fr auto auto;
        height: 100%;
        .content {
            grid-area: content;
        }
        .value {
            grid-area: value;
        }
        .bar {
            grid-area: bar;
            min-height: 30px;
        }
        .reading {
            grid-area: reading;
        }
        .footer {
            grid-area: footer;
        }
        .footer-status {
            grid-row: 4 / span 2;
            grid-column: 3;
            display: flex;
            justify-content: flex-end;

            &.small {
                grid-area: status;
                justify-content: flex-start;
            }
        }
    }
`;

const CardFooter = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    margin-top: auto;
`;

const Value = styled.div<{ color?: string }>`
    margin-left: auto;
    font-size: 25px;
    font-weight: 600;
    align-self: flex-start;
    color: ${({ color }) => color ?? "#95D046"};
`;

const MinMaxRow = styled.div`
    display: flex;
    gap: 20px;
    font-size: 16px;
    color: #828fab;
`;

const MinMaxValue = styled.span`
    color: #fff;
    font-weight: 600;
`;

const StatusValue = styled.span<{ color?: string }>`
    font-size: 16px;
    font-weight: 500;
    color: ${({ color }) => color ?? "#95D046"};

    &.icon {
        position: relative;
        padding-left: 30px;
        color: #4c4c4c;
        font-size: 14px;
        &::before {
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            content: "";
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background-color: ${({ color }) => color ?? "#95D046"};
        }
    }
`;

const LastActive = styled.div`
    font-size: 15px;
    color: #828fab;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CustomPopover = styled(Popover)`
    width: 330px;
`;

// Props for the card
interface SensorCardProps {
    chartType: ChartType;
    device: DeviceInfo;
    widgetId: string;
}
type CardType =
    | "any"
    | "temperature"
    | "humidity"
    | "ambient-light"
    | "IAQ"
    | "motion";

type SensorReading = {
    lowest: number | null;
    highest: number | null;
    value: number | null;
    lastActive: number | null;
};

type ReadingCardProps = {
    device: DeviceInfo;
    reading: SensorReading;
};

type MotionCardProps = {
    motion: { lastMotionAt: number };
};

type IAQStatus = {
    offset: number;
    color: string;
    ranges: [number | undefined, number];
    label: string;
};

type IAQStatusRange = Omit<IAQStatus, "offset">;

const IAQDefaultMin = 0;
const IAQDefaultMax = 500;
const IAQStatusRanges: IAQStatusRange[] = [
    // 0-50
    { color: "#6FDE42", ranges: [0, 50], label: "Excellent" },
    // 51-100
    { color: "#A1CD62", ranges: [51, 100], label: "Good" },
    // 101-150
    { color: "#FFFD54", ranges: [101, 150], label: "Lightly Polluted" },
    // 151-200
    { color: "#EC8332", ranges: [151, 200], label: "Moderately Polluted" },
    // 201-250
    { color: "#E63323", ranges: [201, 250], label: "Heavily Polluted" },
    // 251-350
    { color: "#8A1C4B", ranges: [251, 350], label: "Severely Polluted" },
    // >350
    { color: "#5E350F", ranges: [undefined, 350], label: "Extremely Polluted" },
];

/** From this IAQ value through max, bar uses Extremely Polluted only (flat brown). */
const IAQ_EXTREME_FLAT_FROM = 400;

/** Gardient stops through Heavily (250), then flat Extremely from {@link IAQ_EXTREME_FLAT_FROM} to max. */
function buildIAQColorStatusGradient(
    rows: readonly IAQStatusRange[],
    lowerBound: number,
    upperBound: number
) {
    const scale = upperBound - lowerBound || 1;
    /** Map a raw value to [0, 1] for gradient stop offsets (linear, clamped). */
    const n = (value: number) => {
        // 1. Shift so lowerBound maps to 0: position along the span [lowerBound, upperBound].
        const t = (value - lowerBound) / scale;
        // 2. Clamp below: values under lowerBound would be negative → use 0.
        const clampedLow = Math.max(0, t);
        // 3. Clamp above: values over upperBound would exceed 1 → use 1.
        return Math.min(1, clampedLow);
    };
    const extremeColor = rows.at(-1)!.color;
    const stops: { offset: number; color: string }[] = [];

    const firstEnd = rows[0].ranges[1];
    stops.push(
        { offset: n(0), color: rows[0].color },
        { offset: n(firstEnd), color: rows[0].color }
    );

    // Add gradient stops for the remaining ranges (Good, Lightly Polluted, Moderately Polluted, Heavily Polluted).
    for (let i = 1; i <= 4; i++) {
        stops.push({ offset: n(rows[i].ranges[1]), color: rows[i].color });
    }

    const extremeStart = Math.min(IAQ_EXTREME_FLAT_FROM, upperBound);
    const atExtreme = Math.max(lowerBound, extremeStart);

    // Add gradient stop for the extreme range (Extremely Polluted).
    // Start from here color will be same as Extremely Polluted.
    stops.push({ offset: n(atExtreme), color: extremeColor });

    if (upperBound > atExtreme) {
        // Add gradient stop for the upper bound (Extremely Polluted).
        // Start from here color will be same as Extremely Polluted.
        stops.push({ offset: n(upperBound), color: extremeColor });
    }

    const tail = stops.at(-1);
    if (tail !== undefined && tail.offset < 1) {
        // Covers the edge case
        stops.push({ offset: 1, color: extremeColor });
    }
    return stops;
}

function getIaqStatusForValue(value: number): IAQStatusRange {
    let status = IAQStatusRanges[0];
    for (const element of IAQStatusRanges) {
        const {
            ranges: [start, end],
        } = element;
        if (
            (isUndefined(start) && value > end) ||
            (!isUndefined(start) && start <= value && value <= end)
        ) {
            status = element;
            break;
        }
    }
    return status;
}

const getCardType = (widgetChartType: ChartType, senorType: string) => {
    let type = "any";
    if (
        widgetChartType?.includes("CARD") &&
        widgetChartType !== ChartType.CARD_NUMBER_ONLY
    ) {
        switch (senorType.trim().toLowerCase()) {
            case "temperature sensor": {
                type = "temperature";
                break;
            }
            case "motion sensor": {
                type = "motion";
                break;
            }
            case "humidity sensor": {
                type = "humidity";
                break;
            }
            case "ambient light sensor": {
                type = "ambient-light";
                break;
            }
            case "iaq sensor": {
                type = "IAQ";
                break;
            }
        }
    }

    return type as CardType;
};

const IAQStatusTooltip = ({
    id,
    statusRanges,
}: {
    id: string;
    statusRanges: Omit<IAQStatus, "offset">[];
}) => {
    const [show, setShow] = useState(false);
    const [target, setTarget] = useState(null);
    const ref = useRef<HTMLDivElement>(null);
    const handleShow = (event: any) => {
        setShow(!show);
        setTarget(event.target);
    };

    const displayRanges = ([value1, value2]: (number | undefined)[]) => {
        return isUndefined(value1) ? `>${value2}` : `${value1}-${value2}`;
    };

    return (
        <div ref={ref}>
            <InfoOutlined
                onMouseOver={handleShow}
                onMouseOut={handleShow}
                className="text-primary-blue-4"
            />
            <Overlay
                show={show}
                target={target}
                placement="auto"
                container={ref.current}
                containerPadding={20}
                flip={true}
            >
                <CustomPopover id={id} style={{ width: 330 }}>
                    <Popover.Content>
                        <div className="text-left">
                            {statusRanges.map(({ color, label, ranges }) => (
                                <span
                                    key={label}
                                    className="d-flex justify-content-between align-items-center py-2"
                                >
                                    <StatusValue color={color} className="icon">
                                        {label}
                                    </StatusValue>
                                    <span className="text-primary-blue-4">
                                        {displayRanges(ranges)}
                                    </span>
                                </span>
                            ))}
                        </div>
                    </Popover.Content>
                </CustomPopover>
            </Overlay>
        </div>
    );
};

const AnyCard = ({ device, reading }: ReadingCardProps) => {
    const { accuracy, unit } = device;

    const hasCurrentReading =
        Boolean(reading.lastActive) && reading.value !== null;
    const valueDisplay = hasCurrentReading
        ? formatReadingValue(reading.value as number, accuracy, unit).toString()
        : "-";
    return (
        <>
            <SlotWrapper slot="value">
                <div className="d-flex flex-column align-items-end text-end">
                    <SensorContent>
                        <Value className="text-primary-green-1">
                            {valueDisplay}
                        </Value>
                    </SensorContent>
                    <ReadingTimeText fontSize={14} className="mt-1">
                        {getReadingDateTimeToDisplay(reading.lastActive)}
                    </ReadingTimeText>
                </div>
            </SlotWrapper>
            <SlotWrapper slot="bar">
                <div></div>
            </SlotWrapper>
            <SlotWrapper slot="reading">
                <div>
                    <MinMaxRow>
                        <span>
                            Min:{" "}
                            <MinMaxValue>
                                {reading.lowest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.lowest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>
                            Max:{" "}
                            <MinMaxValue>
                                {reading.highest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.highest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>(Last 24 hrs)</span>
                    </MinMaxRow>
                </div>
            </SlotWrapper>
        </>
    );
};

const TemperatureCard = ({ device, reading }: ReadingCardProps) => {
    const { accuracy: _accuracy, unit, min, max } = device;
    const accuracy = _accuracy ?? 0;
    const colorOffsets = [
        { offset: 0, color: "#3D86A4" },
        { offset: 0.25, color: "#61AECC" },
        { offset: 0.495, color: "#87DDFF" },
        { offset: 0.655, color: "#FFF157" },
        { offset: 0.795, color: "#FFA200" },
        { offset: 1, color: "#FF0000" },
    ];
    const labelPositions = [
        asPercentage(0),
        asPercentage(0.25),
        asPercentage(0.5),
        asPercentage(0.75),
        asPercentage(1),
    ];
    const markerColor = "#7ED2F3";
    const formatFahrenheit = (value: number) => {
        const fahrenheitValue = (value * 9) / 5 + 32;
        return formatValueWithUnit(
            addDecimalPlace(fahrenheitValue, accuracy),
            "°F"
        );
    };

    const hasCurrentReading =
        Boolean(reading.lastActive) && reading.value !== null;
    const getReadingValue = hasCurrentReading
        ? formatReadingValue(reading.value as number, accuracy, unit).toString()
        : "-";

    return (
        <>
            <SlotWrapper slot="value">
                <div className="d-flex flex-column align-items-end text-end">
                    <SensorContent>
                        <Value color={markerColor}>{getReadingValue}</Value>
                    </SensorContent>
                    <ReadingTimeText fontSize={14} className="mt-1">
                        {getReadingDateTimeToDisplay(reading.lastActive)}
                    </ReadingTimeText>
                </div>
            </SlotWrapper>
            <SlotWrapper slot="reading">
                <div>
                    <MinMaxRow>
                        <span>
                            Min:{" "}
                            <MinMaxValue>
                                {reading.lowest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.lowest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>
                            Max:{" "}
                            <MinMaxValue>
                                {reading.highest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.highest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>(Last 24 hrs)</span>
                    </MinMaxRow>
                </div>
            </SlotWrapper>
            <SlotWrapper slot="bar">
                <div>
                    <CardBar
                        min={min ?? DefaultMin}
                        max={max ?? DefaultMax}
                        value={
                            getReadingValue === "-" ? null : (reading.value as number)
                        }
                        units={[unit, formatFahrenheit]}
                        colorOffsets={colorOffsets}
                        labels={labelPositions}
                    />
                </div>
            </SlotWrapper>
        </>
    );
};

const HumidityCard = ({ device, reading }: ReadingCardProps) => {
    const { accuracy, unit, min, max } = device;
    const colorOffsets = [
        { offset: 0, color: "#ACE9C0" },
        { offset: 1, color: "#36AF5E" },
    ];
    const markerColor = "#6CC98B";

    const hasCurrentReading =
        Boolean(reading.lastActive) && reading.value !== null;
    const getReadingValue = hasCurrentReading
        ? formatReadingValue(reading.value as number, accuracy, unit).toString()
        : "-";

    return (
        <>
            <SlotWrapper slot="value">
                <div className="d-flex flex-column align-items-end text-end">
                    <SensorContent>
                        <Value color={markerColor}>{getReadingValue}</Value>
                    </SensorContent>
                    <ReadingTimeText fontSize={14} className="mt-1">
                        {getReadingDateTimeToDisplay(reading.lastActive)}
                    </ReadingTimeText>
                </div>
            </SlotWrapper>
            <SlotWrapper slot="reading">
                <div>
                    <MinMaxRow>
                        <span>
                            Min:{" "}
                            <MinMaxValue>
                                {reading.lowest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.lowest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>
                            Max:{" "}
                            <MinMaxValue>
                                {reading.highest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.highest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>(Last 24 hrs)</span>
                    </MinMaxRow>
                </div>
            </SlotWrapper>
            <SlotWrapper slot="bar">
                <div>
                    <CardBar
                        min={min ?? DefaultMin}
                        max={max ?? DefaultMax}
                        value={
                            getReadingValue === "-" ? null : (reading.value as number)
                        }
                        units={[unit]}
                        colorOffsets={colorOffsets}
                    />
                </div>
            </SlotWrapper>
        </>
    );
};

const AmbientLightCard = ({ device, reading }: ReadingCardProps) => {
    const { accuracy, unit, min, max } = device;
    const colorOffsets = [
        { offset: 0, color: "#FFE589" },
        { offset: 1, color: "#DBA417" },
    ];
    const markerColor = "#F3D065";

    const hasCurrentReading =
        Boolean(reading.lastActive) && reading.value !== null;
    const getReadingValue = hasCurrentReading
        ? formatReadingValue(reading.value as number, accuracy, unit).toString()
        : "-";

    return (
        <>
            <SlotWrapper slot="value">
                <div className="d-flex flex-column align-items-end text-end">
                    <SensorContent>
                        <Value color={markerColor}>{getReadingValue}</Value>
                    </SensorContent>
                    <ReadingTimeText fontSize={14} className="mt-1">
                        {getReadingDateTimeToDisplay(reading.lastActive)}
                    </ReadingTimeText>
                </div>
            </SlotWrapper>
            <SlotWrapper slot="reading">
                <div>
                    <MinMaxRow>
                        <span>
                            Min:{" "}
                            <MinMaxValue>
                                {reading.lowest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.lowest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>
                            Max:{" "}
                            <MinMaxValue>
                                {reading.highest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.highest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>(Last 24 hrs)</span>
                    </MinMaxRow>
                </div>
            </SlotWrapper>
            <SlotWrapper slot="bar">
                <div>
                    <CardBar
                        min={min ?? DefaultMin}
                        max={max ?? DefaultMax}
                        value={
                            getReadingValue === "-" ? null : (reading.value as number)
                        }
                        units={[unit]}
                        colorOffsets={colorOffsets}
                    />
                </div>
            </SlotWrapper>
        </>
    );
};

const IAQCard = ({ device, reading }: ReadingCardProps) => {
    const { accuracy, unit, min, max, deviceId, said } = device;
    const barRef = useRef<HTMLDivElement>(null);
    const iaqMin = min ?? IAQDefaultMin;
    const iaqMax = max ?? IAQDefaultMax;
    const lowerBound = Math.min(iaqMin, iaqMax);
    const upperBound = Math.max(iaqMin, iaqMax);

    const colorStatus = useMemo(
        () =>
            buildIAQColorStatusGradient(IAQStatusRanges, lowerBound, upperBound),
        [lowerBound, upperBound]
    );

    const minWidthBar = 400;
    const { boxWidth } = useChartWidth({
        ref: barRef,
        occupiedWidth: 0,
    });
    const isLessWidth = boxWidth < minWidthBar;

    const hasCurrentReading =
        Boolean(reading.lastActive) && reading.value !== null;
    const readingValue = hasCurrentReading ? (reading.value as number) : 0;

    const getReadingValue = hasCurrentReading
        ? formatReadingValue(readingValue, accuracy, unit).toString()
        : "-";

    const currentStatus = useMemo(() => {
        if (!hasCurrentReading) return null;
        return getIaqStatusForValue(readingValue);
    }, [hasCurrentReading, readingValue]);

    return (
        <>
            <SlotWrapper slot="value">
                <div className="d-flex flex-column align-items-end text-end">
                    <SensorContent>
                        <Value color={"#ffffff"}>{getReadingValue}</Value>
                    </SensorContent>
                    <ReadingTimeText fontSize={14} className="mt-1">
                        {getReadingDateTimeToDisplay(reading.lastActive)}
                    </ReadingTimeText>
                </div>
            </SlotWrapper>
            <SlotWrapper slot="reading">
                <div>
                    <MinMaxRow>
                        <span>
                            Min:{" "}
                            <MinMaxValue>
                                {reading.lowest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.lowest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>
                            Max:{" "}
                            <MinMaxValue>
                                {reading.highest === null
                                    ? "-"
                                    : formatReadingValue(
                                        reading.highest,
                                        accuracy,
                                        unit
                                    )}
                            </MinMaxValue>
                        </span>
                        <span>(Last 24 hrs)</span>
                    </MinMaxRow>
                </div>
            </SlotWrapper>
            <SlotWrapper slot="bar">
                <div ref={barRef}>
                    <CardBar
                        min={iaqMin}
                        max={iaqMax}
                        value={getReadingValue === "-" ? null : readingValue}
                        units={[unit]}
                        colorOffsets={colorStatus}
                    />
                </div>
            </SlotWrapper>
            <div className={`footer-status ${isLessWidth ? "small" : ""}`}>
                <StatusValue className="mr-2" color={"#ffffff"}>
                    {getReadingValue === "-" || !currentStatus
                        ? "-"
                        : currentStatus.label}
                </StatusValue>
                {getReadingValue !== "-" && currentStatus && (
                    <IAQStatusTooltip
                        statusRanges={IAQStatusRanges}
                        id={`${deviceId}-${said}`}
                    />
                )}
            </div>
        </>
    );
};

const MotionCard = ({ motion }: MotionCardProps) => {
    const status = useMemo(() => {
        if (motion.lastMotionAt > 0) {
            return {
                label: "Motion Detected",
                time: moment(motion.lastMotionAt * 1000).calendar(),
            };
        }
        return { label: "No Motion Detected", time: "" };
    }, [motion]);

    return (
        <RowBox className="d-flex bar mt-3 justify-content-between bg-secondary px-3 py-2">
            <StatusValue className="text-secondary-red-1">
                {status.label}
            </StatusValue>
            <span>{status.time}</span>
        </RowBox>
    );
};

const CardComponentMap: Record<
    Exclude<CardType, "motion">,
    FC<ReadingCardProps>
> = {
    any: AnyCard,
    temperature: TemperatureCard,
    humidity: HumidityCard,
    "ambient-light": AmbientLightCard,
    IAQ: IAQCard,
};

/**
 * Renders a sensor card component displaying sensor information and readings.
 * It manages the state of sensor readings and refreshes data on demand.
 *
 * @param {SensorCardProps} device - The device object containing sensor details.
 * @param {string} chartType - The type of chart to display for the sensor.
 * @param {string} widgetId - The unique identifier for the widget.
 * @returns {JSX.Element} The rendered sensor card component.
 * @throws {Error} Throws an error if the sensor reading fetch fails.
 */
const SensorCard = ({ device, chartType, widgetId }: SensorCardProps) => {
    const { sensorName, gatewayId, deviceId, sensorType, said, readings } =
        device;
    device.accuracy = device.accuracy ?? 0;

    const refreshRateMs = 30_000;
    const isMountedRef = useRef(true);

    const [sensorReading, setSensorReading] = useState<SensorReading>({
        lowest: toFiniteReadingNumber(readings?.lowest),
        highest: toFiniteReadingNumber(readings?.highest),
        value: null,
        lastActive: null,
    });
    const [motionRow, setMotionRow] = useState<{ lastMotionAt: number }>({
        lastMotionAt: 0,
    });
    const [motionLastActive, setMotionLastActive] = useState<number | null>(
        null
    );
    // Accumulate metric values incrementally; using a ref avoids it being a useCallback dep
    const metricValuesRef = useRef<[number, string][]>([]);

    // Reset accumulated values when device identity changes so stale data never bleeds across devices
    useEffect(() => {
        metricValuesRef.current = [];
    }, [deviceId, gatewayId, said]);

    const [cardType, setCardType] = useState<CardType>("any");

    const defaultSlotConfigs = {
        content: {
            className: "content",
        },
        value: {
            className: "value",
        },
        bar: {
            className: "bar my-2 my-sm-3 my-md-2",
        },
        reading: {
            className: "reading mb-1 mb-md-0",
        },
        footer: {
            className: "footer",
        },
        "footer-status": {
            className: "footer-status",
        },
    };

    const fetchMotionReading = useCallback(async () => {
        if (!deviceId) {
            return;
        }

        const now = moment();
        const startTime = now.clone().subtract(1, "day").unix();
        const endTime = now.unix();

        const values = await fetchSensorMetricData({
            metric: device.metric,
            gatewayId,
            deviceId,
            said,
            startTime,
            endTime,
            previousValues: metricValuesRef.current,
        });

        if (values === null) return;

        metricValuesRef.current = values;

        const latestDetectedEntry = [...values]
            .reverse()
            .find(([, value]) => value === "1");
        setMotionRow({
            lastMotionAt: latestDetectedEntry
                ? Number(latestDetectedEntry[0])
                : 0,
        });
    }, [device.metric, deviceId, gatewayId, said]);

    useEffect(() => {

        setSensorReading((prev) => {
            const nextLow = toFiniteReadingNumber(readings?.lowest);
            const nextHigh = toFiniteReadingNumber(readings?.highest);
            return {
                ...prev,
                lowest: nextLow ?? prev.lowest,
                highest: nextHigh ?? prev.highest,
                value: null,
                lastActive: null,
            };
        });
        setMotionLastActive(null);
    }, [deviceId, gatewayId, said, readings?.lowest, readings?.highest]);

    useEffect(() => {
        if (chartType && sensorType) {
            const cardType = getCardType(chartType, sensorType);
            setCardType(cardType);
        }
    }, [chartType, sensorType]);

    useEffect(() => {
        isMountedRef.current = true;

        const fetchReadingBundle = async () => {
            if (!gatewayId || !deviceId || said === undefined || said === null) {
                return;
            }

            if (cardType === "motion") {
                await fetchMotionReading();
                const { data, status } = await getSensorLastReading(
                    gatewayId,
                    deviceId
                );
                if (!isMountedRef.current) return;

                if (isHttpSuccess(status)) {
                    const ts = (data as { ts?: unknown } | undefined)?.ts;
                    setMotionLastActive(toFiniteReadingNumber(ts));
                } else {
                    setMotionLastActive(null);
                }
                return;
            }

            const [readingWithoutRange, readingWithRange] = await Promise.all([
                getSensorLastReading(gatewayId, deviceId),
                getSensorLastReading(gatewayId, deviceId, "24h"),
            ]);
            if (!isMountedRef.current) return;

            if (isHttpSuccess(readingWithoutRange.status)) {
                const readingWithoutRangeData: any = readingWithoutRange.data ?? {};
                const value = readingWithoutRangeData?.value?.[said];
                const ts = readingWithoutRangeData?.ts;
                setSensorReading((prev) => ({
                    ...prev,
                    value: toFiniteReadingNumber(value),
                    lastActive: toFiniteReadingNumber(ts),
                }));
            } else {
                setSensorReading((prev) => ({
                    ...prev,
                    value: null,
                    lastActive: null,
                }));
            }

            if (isHttpSuccess(readingWithRange.status)) {
                const rangeData: any = readingWithRange.data ?? {};
                const lowest = rangeData?.min?.[said];
                const highest = rangeData?.max?.[said];
                setSensorReading((prev) => {
                    const nextLow = toFiniteReadingNumber(lowest);
                    const nextHigh = toFiniteReadingNumber(highest);
                    return {
                        ...prev,
                        lowest: nextLow ?? prev.lowest,
                        highest: nextHigh ?? prev.highest,
                    };
                });
            }
        };

        void fetchReadingBundle();
        const interval = setInterval(fetchReadingBundle, refreshRateMs);

        return () => {
            isMountedRef.current = false;
            clearInterval(interval);
        };
    }, [
        cardType,
        deviceId,
        fetchMotionReading,
        gatewayId,
        refreshRateMs,
        said,
    ]);

    const renderCard = () => {
        if (cardType === "motion") {
            return <MotionCard motion={motionRow} />;
        }
        const Component = CardComponentMap[cardType];
        return <Component device={device} reading={sensorReading} />;
    };

    return (
        <SingleCardContent
            id={`single-sensor-${widgetId}`}
            className="grid"
            $hasFooter={cardType === "motion"}
        >
            <SlotProvider slots={defaultSlotConfigs}>
                <SlotWrapper slot="content">
                    <SensorContent>
                        <IconCircle className="mr-2">
                            <SensorIcon sensorType={sensorType} cls={device.cls} />
                        </IconCircle>
                        <SensorInfo>
                            <SensorLabel aria-label="sensor-name">
                                {sensorName}
                            </SensorLabel>
                            <SensorType aria-label="sensor-type">
                                {sensorType}
                            </SensorType>
                        </SensorInfo>
                    </SensorContent>
                </SlotWrapper>
                {cardType && renderCard()}
                {cardType === "motion" && (
                    <SlotWrapper slot="footer">
                        <CardFooter>
                            <LastActive>
                                Last Updated:{" "}
                                {motionLastActive
                                    ? dateTimeFormatter.calendar(
                                        motionLastActive * 1000
                                    )
                                    : "-"}
                            </LastActive>
                        </CardFooter>
                    </SlotWrapper>
                )}
            </SlotProvider>
        </SingleCardContent>
    );
};

export default SensorCard;
